import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { emailOptionsFromTemplates } from '../../invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { splitEmails } from '../../invoices/utils';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../shared/hooks/usePostQuery';
import {
    InvoicingMessage,
    OrderSendRequest,
    OrderSendResult,
    SendByType,
    SendEmailOptions,
} from '../../invoices/book-and-send/types';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { SendOrderDialog, SendOrderFormData } from './SendOrderDialog';

async function registerAndSendOrder(request: OrderSendRequest) {
    return await internalApiClient.post<OrderSendResult>(`/sales/order/Send`, request);
}

interface GetInformationOrderResult {
    emailOptions: SendEmailOptions;
    messages: InvoicingMessage[];
    exceededSendingLimit: boolean;
}

function SendOrderDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const { state = {} } = location;
    const { draftId } = state;

    const [sending, setSending] = useState<boolean>(false);

    const { loading, data: information } = usePostQuery<GetInformationOrderResult>(
        `/sales/order/GetInformation`,
        {
            orderId: draftId,
        },
        !draftId
    );
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.Order);

    const loadingTexts = useLocalization(textIds);

    const transformedInformation = useMemo(() => {
        if (!information) {
            return undefined;
        }

        return {
            ...information,
            sendByType: SendByType.undefined,
            emailOptions: {
                ...information.emailOptions,
                recipients: information.emailOptions.recipients.join(' '),
                recipientsCc: information.emailOptions.recipientsCc.join(' '),
                recipientsBcc: information.emailOptions.recipientsBcc.join(' '),
                includePdfLink: information.emailOptions.includePdfLink,
                includePdfAsAttachment: information.emailOptions.includePdfAsAttachment,

                ...emailOptionsFromTemplates(templates),
            },
            ...emailTemplateFormState(templates),
        };
    }, [information, templates]);

    async function registerAndSend(values: SendOrderFormData) {
        try {
            setSending(true);
            const result = await registerAndSendOrder({
                sendByType: values.sendByType,
                orderId: draftId,
                showPdf: values.emailOptions.showPdf,
                emailOptions: {
                    ...values.emailOptions,
                    recipients: splitEmails(values.emailOptions.recipients),
                    recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                    recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                },
            });

            setSending(false);
            toast.success(t`TOAST_ORDER_SEND_SUCCESS`);

            if (values.emailOptions.showPdf && result.data.pdfLink) {
                window.top?.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            refreshContentFrameAfterSuccess();
            hide();
        } catch (error) {
            setSending(false);
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname + location.search);
    }

    if (!draftId) {
        return null;
    }

    return (
        <SendOrderDialog
            open={location.hash === '#book-order'}
            information={transformedInformation}
            loading={loading || loadingTexts || loadingTemplates || sending}
            onRegisterAndSend={registerAndSend}
            onHide={hide}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_ORDER_HEADER',
    'FORM_BOOKANDSEND_ORDER_TEXT',
    'FORM_BOOKANDSEND_SHOW_ORDER',
    'FORM_BOOKANDSEND_ORDER_REGISTERASSENT',
    'FORM_BOOKANDSEND_ORDER_REGISTERANDSEND',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SIGNEE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'FORM_BOOKANDSEND_ERRORS_HEADER',
    'TOAST_ORDER_SEND_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
];

export default SendOrderDialogContainer;
